import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { PROFILE, STRENGTHS_PROFILE, CV_METRICS } from "@/data/portfolio";
import { DA } from "@/data/da";
import { KOLDING_COVER, KOLDING_CV } from "@/data/kolding";

const SITE = "https://sinthunathan.com";

const PARAGRAPHS = [
  `You are looking for an owner of customer insights who can build the research system from scratch, with the use of AI, and turn continuous data from your target audience into growth. I have built exactly that, twice: once inside a global robotics company, once with my own hands.`,
  `At Universal Robots I ran global discovery research to define customer needs, pain points and opportunities across Asia, Europe and the US; built customer journey maps, archetypes and requirements for different target groups; validated assumptions and tested ideas alongside the UX architect and designers; and benchmarked our value proposition against key competitors in a model our CEO used for strategy.`,
  `At Tennis Club Odense I set up analytics in a digital product for continuous improvement, met customers in person and online, built the community and feedback loop, and ran the beta: sold out, NPS +66, reach +1,186%.`,
  `Your digitalisation is data-driven and iterative. So is my research, hands-on with AI at every stage, human in the loop. Give me fifteen minutes and I will show you how I would set up Bixter's insight-to-growth loop in the first 90 days. My portfolio, ${SITE}, measures its own funnel, so you will be in the data before we meet.`,
];

const PROOF = [
  ["CEO-level", "value-proposition benchmark used for strategy"],
  ["200+", "customer interviews · Asia, Europe, US"],
  ["NPS +66", "research system built from scratch with AI"],
];

const EN = {
  label: "Application · Customer Insights & Growth Researcher, Digital Product Development",
  role: "Customer Insights & Growth Researcher",
  attn: "Bixter · Attn. Azadeh Sabouri, Digital Product Development",
  heading: "Your next owner of customer insights.",
  dear: "Dear Azadeh,",
  paragraphs: PARAGRAPHS,
  proof: PROOF,
  regards: "Kind regards,",
  enclosed: "Enclosed: CV · Portfolio",
  references: `References available at ${SITE}/#testimonials`,
  locale: "en-GB",
};

function LangToggle({ lang, base }) {
  return (
    <div data-testid="lang-toggle" className="inline-flex rounded-full border border-[#14352a] overflow-hidden text-xs font-medium">
      {["en", "da"].map((l) => (
        <Link
          key={l}
          to={`${base}${base.includes("?") ? "&" : "?"}lang=${l}`}
          data-testid={`lang-${l}`}
          className={`px-3 h-10 inline-flex items-center ${lang === l ? "bg-[#14352a] text-white" : "hover:bg-[#f2ece4]"}`}
        >
          {l.toUpperCase()}
        </Link>
      ))}
    </div>
  );
}

export default function CoverLetter() {
  const [params] = useSearchParams();
  const variant = params.get("for") || "";
  const lang = params.get("lang") === "da" || ["kolding", "proces"].includes(variant) ? "da" : "en";
  const t = ["kolding", "proces"].includes(variant) ? KOLDING_COVER : lang === "da" ? DA.cover : EN;
  const metrics = ["kolding", "proces"].includes(variant) ? KOLDING_CV.metrics : lang === "da" ? DA.cv.metrics : CV_METRICS;
  useEffect(() => {
    document.title = lang === "da" ? "Ansøgning, Sinthu Nathan" : "Cover letter, Sinthu Nathan";
  }, [lang]);

  return (
    <div data-testid="cover-letter-page" className="min-h-screen bg-[#e9e4dc] print:bg-white py-8 print:py-0">
      <div className="no-print max-w-[210mm] mx-auto mb-4 flex items-center justify-between gap-3 px-2">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#55625b] hover:text-[#9a7218]">
          <ArrowLeft className="h-4 w-4" /> Back to portfolio
        </Link>
        <div className="flex items-center gap-2">
        <LangToggle lang={lang} base="/cover-letter" />
        <button
          data-testid="cover-letter-download-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 h-10 px-5 rounded-full btn-guide text-sm font-medium"
        >
          <Printer className="h-4 w-4" /> {lang === "da" ? "Gem som PDF" : "Save as PDF"}
        </button>
        </div>
      </div>

      <article
        data-testid="cover-letter-sheet"
        className="print-sheet mx-auto bg-[#faf8f5] text-[#14352a] w-[210mm] h-[297mm] overflow-hidden shadow-xl print:shadow-none flex flex-col"
      >
        <header data-testid="cover-letter-header" className="bg-[#14352a] text-[#faf8f5] px-[12mm] pt-[9mm] pb-[7mm] border-b-[3px] border-[#9a7218]">
          <div className="flex items-start gap-5">
            <img src="/portrait.jpg" alt={PROFILE.name} className="h-[26mm] w-[26mm] object-cover rounded-sm border-2 border-[#d9a92e] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-mono-label text-[8.5px] text-[#d9a92e]">{t.label}</div>
              <h1 className="mt-1 font-display text-[30px] leading-none tracking-tight">{PROFILE.name}</h1>
              <p className="mt-1.5 text-[12px] text-[#faf8f5]/90">{t.role}</p>
              <p className="font-mono-label text-[8px] text-[#faf8f5]/60 mt-1">{PROFILE.location}</p>
            </div>
            <div className="text-right font-mono-label text-[8px] text-[#faf8f5]/75 leading-[1.9] shrink-0">
              {PROFILE.phone}<br />{PROFILE.email}<br />{PROFILE.linkedin.replace("https://www.", "")}<br />
              <span className="text-[#d9a92e]">{PROFILE.domain}</span>
            </div>
          </div>
        </header>

        <section data-testid="cover-letter-metrics" className="grid grid-cols-4 bg-[#f2ece4] border-b border-[#e5e0d8] divide-x divide-[#e5e0d8]">
          {metrics.map((m, i) => (
            <div key={i} className="px-[6mm] py-4">
              <div className="font-display text-[24px] leading-none text-[#14352a]">{m.value}</div>
              <div className="mt-1.5 font-mono-label text-[7px] text-[#55625b] leading-snug">{m.label}</div>
            </div>
          ))}
        </section>

        <main className="flex-1 px-[12mm] pt-8 text-[13.5px] leading-[1.85] text-[#14352a]">
          <div className="flex items-baseline justify-between gap-4 font-mono-label text-[8.5px] text-[#86928a]">
            <span>{t.attn}</span>
            <span>Odense</span>
          </div>
          <h2 className="mt-8 font-display text-[24px] leading-snug border-l-[3px] border-[#9a7218] pl-3">
            {t.heading}
          </h2>
          <p className="mt-5">{t.dear}</p>
          <div className="mt-5 space-y-5">
            {t.paragraphs.slice(0, 3).map((p, i) => (
              <p key={i} data-testid={`cover-letter-p-${i}`}>{p}</p>
            ))}
          </div>
          <p data-testid="cover-letter-p-3" className="mt-5">{t.paragraphs[3]}</p>
          <p className="mt-6">{t.regards}</p>
          <div className="mt-3 flex items-end justify-between gap-6">
            <div>
              <div className="font-display italic text-[20px] leading-none">Sinthu Nathan</div>
              <div className="mt-1.5 font-mono-label text-[8px] text-[#86928a]">{t.role} · Odense</div>
            </div>
            <div className="font-mono-label text-[8px] text-[#86928a] text-right leading-relaxed">
              {t.enclosed}<br />
              <span className="text-[#9a7218]">{SITE}</span>
            </div>
          </div>
        </main>

        <footer className="mx-[12mm] mb-[7mm] border-t border-[#e5e0d8] pt-2.5 flex items-center justify-between gap-4 font-mono-label text-[7.5px] text-[#86928a]">
          <span>{t.references}</span>
          <span data-testid="cover-letter-strengths" className="text-[#9a7218]">CliftonStrengths: {STRENGTHS_PROFILE.top5.map((s) => s.name).join(" · ")}</span>
          <span>{PROFILE.location}</span>
        </footer>
      </article>
    </div>
  );
}
